/**
 * Bracket ladders — the port of views/bracket_viz.ejs.
 *
 * Two visualisations, the same two the EJS page draws: the ORDINARY ladder (federal, with the standard
 * deduction as a 0% row) and the PREFERENTIAL ladder (long-term gains and qualified dividends at 0/15/20%),
 * plus the state ladder when the profile's state is progressive.
 *
 * This is markup, not a second computation: every figure comes from `bracketViz` in the tax payload, which
 * api/pages.js computes once through taxestimate.bracketVisualization.
 *
 * The words, the colours, the "% of tax" head and the two badges are the partial's, not a paraphrase: the
 * EJS page is the reference, and a ladder of numbers a reader compares row by row shows every invented
 * label as a difference.
 */
import { Fragment } from 'react';
import { money } from '../../format';

type LadderRow = {
  lo: number;
  hi: number | null;
  rate: number;
  income: number;
  tax: number;
  occupied: boolean;
  divIncome?: number;
  divTax?: number;
  filledByOrdinary?: boolean;
  name?: string;
};

type FederalViz = {
  ladder: LadderRow[];
  breakdown: LadderRow[];
  headroom?: { amount: number; rate: number; at: number } | null;
  marginalRate?: number;
  gross?: number;
  deductionApplied?: number;
  stdDeduction?: number;
  ordinaryTaxable?: number;
  lossDeducted?: number;
  lossCarryforward?: number;
  netSt?: number;
  netLt?: number;
  preferential?: {
    income: number;
    ladder: LadderRow[];
    occupied: LadderRow[];
    tax?: number;
    zeroTierFilled?: boolean;
    headroom?: { amount: number; rate: number; at: number } | null;
  } | null;
};

export type BracketViz = { federal: FederalViz; state?: { name: string; breakdown: LadderRow[] } | null } | null;

/** views/bracket_viz.ejs's fmtPct: 24, 9.3 — a whole percent keeps no decimals (the % is added by the caller). */
const fmtPct = (r: number) => (((r * 100) % 1 === 0 ? (r * 100).toFixed(0) : (r * 100).toFixed(1)));

/** views/bracket_viz.ejs's rateColor, rate for rate — the chip and the bar use it. */
const rateColor = (r: number) => {
  const pct = r * 100;
  if (pct <= 2) return '#3d5a9e';
  if (pct <= 6) return 'var(--accent)';
  if (pct <= 10) return '#7c5ef0';
  if (pct <= 15) return '#9d5ef0';
  if (pct <= 20) return '#c05599';
  if (pct <= 25) return '#d64f6e';
  if (pct <= 33) return '#e05545';
  return '#f0564f';
};

/** A 0% tier reads as "no tax", not as a low rate — it gets its own colour, as tierColor() in the partial. */
const tierColor = (r: number) => (r === 0 ? 'var(--green)' : rateColor(r));

/** The partial's rangeLabel: "… – …" for a closed bracket, "… and up" for the last one. */
const rangeLabel = (s: LadderRow) => (s.hi ? `${money(s.lo)} – ${money(s.hi)}` : `${money(s.lo)} and up`);

function Ladder({
  title,
  sub,
  headLabel,
  thirdLabel,
  ladder,
  totalTax,
  deductionRow,
  axisMax,
  marginal,
  colorOf = rateColor,
  badge,
}: {
  title: React.ReactNode;
  sub?: React.ReactNode | null;
  headLabel: string;
  thirdLabel: string;
  ladder: LadderRow[];
  totalTax: number;
  deductionRow?: { label: string; amount: number; width: number } | null;
  axisMax: number;
  marginal?: LadderRow | null;
  colorOf?: (rate: number) => string;
  badge?: (s: LadderRow) => string | null;
}) {
  return (
    <div className="vbracket">
      <div className="vbracket-title">
        {title}
        {sub == null ? null : <div className="vbracket-sub">{sub}</div>}
      </div>
      <div className="vbracket-head">
        <span />
        <span>{headLabel}</span>
        <span>{thirdLabel}</span>
        <span className="num">Tax</span>
        <span className="num">% of tax</span>
      </div>
      {deductionRow ? (
        <div
          className="vbracket-row vbracket-row-deduction"
          tabIndex={0}
          title={`Standard deduction: ${money(deductionRow.amount)} of income taxed at 0% — the brackets below start above it`}
        >
          <div className="vbracket-color" style={{ background: 'var(--muted)' }} />
          <span className="vbracket-range">0% · {deductionRow.label}</span>
          <div className="vbracket-barwrap">
            <div className="vbracket-bar vbracket-bar-ded" style={{ width: `${deductionRow.width}%` }} />
            <span className="vbracket-val">{money(deductionRow.amount)}</span>
          </div>
          <span className="vbracket-tax num">{money(0)}</span>
          <span className="vbracket-share num">0%</span>
        </div>
      ) : null}
      {ladder.map((s) => {
        const tag = badge ? badge(s) : null;
        return (
          <div
            key={`${s.lo}-${s.rate}`}
            className={
              'vbracket-row' + (s.occupied ? '' : ' vbracket-row-empty') + (marginal === s ? ' vbracket-row-here' : '')
            }
            tabIndex={0}
            title={
              `${fmtPct(s.rate)}% bracket ${rangeLabel(s)}${s.name ? ` (${s.name})` : ''}: ` +
              (s.occupied ? `${money(s.income)} income, ${money(s.tax)} tax` : 'not reached')
            }
          >
            <div
              className="vbracket-color"
              style={{ background: s.occupied ? colorOf(s.rate) : 'var(--border)' }}
            />
            <span className="vbracket-range">
              {/* One text node, as the partial writes it: the comparator reads own text per node, so a split
                  "10" + "% · …" reads as "10 % · …" — a difference the reader never sees. */}
              {`${fmtPct(s.rate)}% · ${rangeLabel(s)}${s.name ? ' *' : ''}`}
              {tag ? <> <span className="vbracket-here">{tag}</span></> : null}
            </span>
            <div className="vbracket-barwrap">
              {s.occupied ? (
                <>
                  <div
                    className="vbracket-bar"
                    style={{
                      width: `${(s.income / axisMax) * 100}%`,
                      background: colorOf(s.rate),
                    }}
                  />
                  <span className="vbracket-val">{money(s.income)}</span>
                </>
              ) : (
                <>
                  <div className="vbracket-bar vbracket-bar-empty" />
                  <span className="vbracket-val">—</span>
                </>
              )}
            </div>
            <span className="vbracket-tax num">{s.occupied ? money(s.tax) : '—'}</span>
            <span className="vbracket-share num">
              {s.occupied && totalTax ? `${((s.tax / totalTax) * 100).toFixed(0)}%` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function BracketVizView({ viz }: { viz: BracketViz }) {
  if (!viz || !viz.federal) return null;
  const fed = viz.federal;
  const fedLadder = fed.ladder || [];
  const occupied = fedLadder.filter((s) => s.occupied);
  const totalTax = occupied.reduce((n, s) => n + (s.tax || 0), 0);
  const deduction = fed.deductionApplied || 0;
  // The axis the partial draws: the deduction band and the OCCUPIED brackets, never the empty ones.
  const axisMax = Math.max(deduction, ...occupied.map((s) => s.income || 0), 1);
  const marginal = occupied.length ? occupied[occupied.length - 1] : null;

  const pref = fed.preferential;
  const prefLadder = pref?.ladder || [];
  const prefOccupied = pref?.occupied || [];
  const prefAxis = Math.max(1, ...prefOccupied.map((s) => s.income || 0));
  const prefTax = pref?.tax ?? prefOccupied.reduce((n, s) => n + (s.tax || 0), 0);

  const showDeduction = (fed.gross ?? 0) > 0 && deduction > 0;

  // views/bracket_viz.ejs writes the whole header on one line: gross, the deduction band and, when they
  // exist, the limitations that explain it — then the two bases the two ladders are drawn from.
  const title = (
    <>
      Federal — gross {money(fed.gross ?? 0)} · standard deduction {money(deduction)} (0%)
      {(fed.stdDeduction ?? 0) > deduction ? <span className="muted"> — limited to your wages</span> : null}
      {(fed.lossDeducted ?? 0) > 0 ? (
        <>
          {' '}· capital-loss deduction {money(fed.lossDeducted ?? 0)}
          {(fed.lossCarryforward ?? 0) > 0 ? (
            <span className="muted"> ({money(fed.lossCarryforward ?? 0)} carries forward)</span>
          ) : null}
        </>
      ) : null}
      {' '}· ordinary taxable {money(fed.ordinaryTaxable ?? 0)}
      {(pref?.income ?? 0) > 0 ? <> · preferential {money(pref?.income ?? 0)}</> : null}
    </>
  );

  const fedSub = !occupied.length ? (
    (fed.gross ?? 0) > 0 ? (
      'All of your income is covered by the standard deduction — nothing is taxable.'
    ) : (fed.lossDeducted ?? 0) > 0 || (fed.netSt ?? 0) < 0 || (fed.netLt ?? 0) < 0 ? (
      'Your gains are net losses this year — nothing is taxed on the ordinary ladder.'
    ) : (
      'No taxable income yet — set your estimated wages and bonus above to fill the ladder.'
    )
  ) : fed.headroom ? (
    <>
      Marginal rate <strong>{fmtPct(fed.marginalRate ?? 0)}%</strong> · {money(fed.headroom.amount)} of headroom
      before the <strong>{fmtPct(fed.headroom.rate)}%</strong> bracket begins at {money(fed.headroom.at)}
    </>
  ) : (
    <>
      Marginal rate <strong>{fmtPct(fed.marginalRate ?? 0)}%</strong> — top bracket, no further headroom
    </>
  );

  const prefSub = pref
    ? !pref.income ? (
        'No long-term gains or qualified dividends this year.'
      ) : prefOccupied.length === 1 && prefOccupied[0].rate === 0 ? (
        <>
          All {money(pref.income)} taxed at <strong>0%</strong> — your ordinary income leaves the 0% tier open.
        </>
      ) : (
        <>
          {pref.zeroTierFilled ? 'The 0% tier is already filled by your ordinary income — ' : ''}
          {/* A Fragment, not a span: views/bracket_viz.ejs writes these as bare text nodes, and the comparator
              reads an element's OWN text — a wrapper span hid the whole list behind one prefix. */}
          {prefOccupied.map((s, i) => (
            <Fragment key={`${s.lo}-${s.rate}`}>
              {i ? ' · ' : ''}
              {money(s.income)} at <strong>{fmtPct(s.rate)}%</strong>
            </Fragment>
          ))}
          {pref.income && pref.headroom ? (
            <>
              <br />
              {money(pref.headroom.amount)} of headroom before the {fmtPct(pref.headroom.rate)}% tier begins at{' '}
              {money(pref.headroom.at)}
            </>
          ) : null}
        </>
      )
    : null;

  const stateNamed = viz.state?.breakdown?.some((s) => s.name) ? viz.state!.breakdown.find((s) => s.name)!.name : null;

  return (
    <>
      <Ladder
        title={title}
        sub={fedSub}
        headLabel="Bracket"
        thirdLabel="Income in bracket"
        ladder={fedLadder}
        totalTax={totalTax}
        axisMax={axisMax}
        marginal={marginal}
        deductionRow={
          showDeduction ? { label: 'Standard deduction', amount: deduction, width: (deduction / axisMax) * 100 } : null
        }
        badge={(s) => (s === marginal ? 'marginal' : null)}
      />

      {pref ? (
        <Ladder
          title="Long-term gains & qualified dividends — 0 / 15 / 20%"
          sub={prefSub}
          headLabel="Tier"
          thirdLabel="Gains & dividends in tier"
          ladder={prefLadder}
          totalTax={prefTax}
          axisMax={prefAxis}
          colorOf={tierColor}
          badge={(s) => (s.filledByOrdinary ? 'filled by ordinary income' : null)}
        />
      ) : null}

      {viz.state && viz.state.breakdown?.length ? (
        <Ladder
          // views/bracket_viz.ejs's own words for the state ladder: it has a title and no sub-line.
          title={`${viz.state.name} — capital gains taxed as ordinary income`}
          sub={null}
          headLabel="Bracket"
          thirdLabel="Income in bracket"
          ladder={viz.state.breakdown}
          totalTax={viz.state.breakdown.reduce((n, s) => n + (s.tax || 0), 0)}
          axisMax={Math.max(1, ...viz.state.breakdown.map((s) => s.income || 0))}
        />
      ) : null}

      {/* The partial's footnote for a named state bracket: the '*'-marked range belongs to it. */}
      {stateNamed ? (
        <div className="muted vbracket-note" style={{ marginTop: 4 }}>
          * {stateNamed}
        </div>
      ) : null}
    </>
  );
}
